import { useInfiniteQuery, useMutation, useQueryClient, type InfiniteData } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight, Bot, Check, CheckCheck, Clock, EyeOff, Keyboard, Layers, Lock, MessageCircle, MessagesSquare, MoreHorizontal, RotateCcw, Sparkles, Trash2, TriangleAlert, User, Workflow, X } from "lucide-react";
import { z } from "zod";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { toast } from "sonner";
import { ui } from "@/app/ui-store";
import { AccountAvatar } from "@/components/brand/brand";
import { Badge, type Tone } from "@/components/ui/badge";
import { Button, IconButton } from "@/components/ui/button";
import { useConfirm } from "@/components/ui/confirm";
import { Checkbox, Select, Tabs, TabsContent, TabsList } from "@/components/ui/controls";
import { Textarea } from "@/components/ui/input";
import { Kbd, Num } from "@/components/ui/misc";
import { Menu, MenuContent, MenuItem, MenuTrigger } from "@/components/ui/overlays";
import { BulkBar, EmptyState, ErrorState, FilterBar, Page, PageHeader, toastUndo, type FilterChip } from "@/components/kit";
import { PostPicker, PostThumb } from "@/features/content/post-quick";
import { deferCommit, UNDO_MS } from "@/features/inbox/deferred";
import { ReauthNotice } from "@/features/settings/reauth";
import { useAccounts } from "@/hooks/data";
import { modKey, useHotkeys } from "@/hooks/use-hotkeys";
import { api, getCurrentOrgId } from "@/lib/api/client";
import { useApiQuery } from "@/lib/api/hooks";
import { invalidate } from "@/lib/api/query-client";
import { fmtDuration, fmtRelative } from "@/lib/format";
import { useT } from "@/lib/i18n";
import { isReauth, toastFailure } from "@/lib/reauth";
import type { Approval, Page as PageOf, PostRef, Rule } from "@/lib/types";
import { cn } from "@/lib/utils";
import { usePublishSelection } from "@/features/copilot/context";
import { TargetPerson } from "@/features/people360/target-person";
import { ApprovalAgentBadge } from "@/features/agents/agent-picker";
import { CommentStatus } from "@/features/comments/comment-status";
import { reasonInWords, windowClosedFor } from "./words";

type Tab = "pending" | "approved" | "rejected";
const KINDS: Approval["kind"][] = ["reply_public", "reply_private", "send_message", "hide", "delete", "bulk"];
type Age = "new" | "day" | "old";
const H = 3600_000;
const PAGE = 25;

/** Filters and the tab live in the URL (shareable, back-button friendly; the ⚠ peek on a rule links here with ?ruleId=). */
export const approvalsSearchSchema = z.object({
  tab: z.enum(["pending", "approved", "rejected"]).optional().catch(undefined),
  postId: z.string().optional().catch(undefined),
  accountId: z.string().optional().catch(undefined),
  ruleId: z.string().optional().catch(undefined),
  kind: z.enum(["reply_public", "reply_private", "send_message", "hide", "delete", "bulk"]).optional().catch(undefined),
  age: z.enum(["new", "day", "old"]).optional().catch(undefined),
});
export type ApprovalsSearch = z.infer<typeof approvalsSearchSchema>;
const NO_FILTERS = { postId: undefined, accountId: undefined, ruleId: undefined, kind: undefined, age: undefined } as const;

function ageRange(age: Age | undefined, now: number): { from?: string; to?: string } {
  if (age === "new") return { from: new Date(now - H).toISOString() };
  if (age === "day") return { from: new Date(now - 24 * H).toISOString(), to: new Date(now - H).toISOString() };
  if (age === "old") return { to: new Date(now - 24 * H).toISOString() };
  return {};
}
const STATUS_QUERY: Record<Tab, string> = { pending: "pending", approved: "approved,edited", rejected: "rejected,expired" };
const MAX = 2200;

const KIND_ICON: Record<Approval["kind"], ReactNode> = {
  reply_public: <MessageCircle />,
  reply_private: <Lock />,
  send_message: <MessagesSquare />,
  hide: <EyeOff />,
  delete: <Trash2 />,
  bulk: <Layers />,
};
const KIND_TONE: Record<Approval["kind"], Tone> = { reply_public: "accent", reply_private: "violet", send_message: "info", hide: "warn", delete: "bad", bulk: "neutral" };

function useNow(ms = 30_000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(id);
  }, [ms]);
  return now;
}

/** A DM can only be sent inside Instagram's 24h window (QA F19): approving it after the window closed would fail. */
export const isWindowClosed = (a: Approval, now: number) =>
  a.kind === "send_message" && a.status === "pending" && a.windowExpiresAt !== undefined && (a.windowExpiresAt === null || Date.parse(a.windowExpiresAt) <= now);

type Pages = InfiniteData<PageOf<Approval>, string | undefined>;

export function ApprovalsPage(_props: { search?: ApprovalsSearch } = {}) {
  const { t } = useT();
  const navigate = useNavigate();
  const confirm = useConfirm();
  // read from the router (not props) so the tab and filters always follow the URL
  const raw = useSearch({ strict: false }) as Record<string, unknown>;
  const search = useMemo(() => approvalsSearchSchema.parse(raw), [raw]);
  const tab: Tab = search.tab ?? "pending";
  const setSearch = (patch: Partial<ApprovalsSearch>) => void navigate({ to: "/approvals", search: { ...search, ...patch }, replace: true });
  const setTab = (v: Tab) => setSearch({ tab: v === "pending" ? undefined : v });
  // the age window is anchored when the filter is chosen (not on every render)
  const range = useMemo(() => ageRange(search.age, Date.now()), [search.age]);
  const query = { status: STATUS_QUERY[tab], postId: search.postId, accountId: search.accountId, ruleId: search.ruleId, kind: search.kind, ...range };
  const filtered = !!(search.postId || search.accountId || search.ruleId || search.kind || search.age);
  const moreCount = [search.postId, search.ruleId, search.age].filter(Boolean).length;
  // picker candidates: every post that has something pending (with counts), independent of the other filters
  const allPending = useApiQuery("approvals", { query: { status: "pending", limit: 100 } }, { staleTime: 15_000 });
  const postOptions = useMemo(() => {
    const m = new Map<string, PostRef>();
    const n = new Map<string, number>();
    for (const a of allPending.data?.items ?? []) {
      if (!a.post) continue;
      m.set(a.post.postId, a.post);
      n.set(a.post.postId, (n.get(a.post.postId) ?? 0) + 1);
    }
    return { posts: [...m.values()], counts: n };
  }, [allPending.data]);
  const { data: accounts = [] } = useAccounts();
  const listKey = ["approvals", getCurrentOrgId(), "infinite", query];
  const list = useInfiniteQuery({
    queryKey: listKey,
    queryFn: ({ pageParam, signal }) => api("approvals", { query: { ...query, cursor: pageParam, limit: PAGE }, signal }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const { data: rules = [] } = useApiQuery("rules", {}, { staleTime: 5 * 60_000 });
  const ruleMap = useMemo(() => new Map(rules.map((r) => [r.id, r])), [rules]);
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(new Set()); // rejected, waiting out the Undo window
  const items = useMemo(() => {
    const seen = new Set<string>();
    const out: Approval[] = [];
    for (const p of list.data?.pages ?? [])
      for (const a of p.items) {
        if (seen.has(a.id) || hiddenIds.has(a.id)) continue;
        seen.add(a.id);
        out.push(a);
      }
    return out;
  }, [list.data, hiddenIds]);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState<Set<string>>(new Set());
  usePublishSelection("approval", selected); // ALP-80: the assistant sees the selected approvals
  const [active, setActive] = useState(0);
  const now = useNow();
  const queryClient = useQueryClient();

  useEffect(() => setActive((a) => Math.min(a, Math.max(0, items.length - 1))), [items.length]);
  useEffect(() => setSelected(new Set()), [tab]);

  const dropFromList = (ids: string[]) => {
    const prev = queryClient.getQueryData<Pages>(listKey);
    if (prev) queryClient.setQueryData<Pages>(listKey, { ...prev, pages: prev.pages.map((p) => ({ ...p, items: p.items.filter((a) => !ids.includes(a.id)) })) });
    setSelected((s) => {
      const n = new Set(s);
      for (const id of ids) n.delete(id);
      return n;
    });
    return prev;
  };

  const decide = useMutation({
    mutationFn: (v: { approval: Approval; decision: "approve" | "reject"; editedText?: string }) =>
      api("decideApproval", { params: { approvalId: v.approval.id }, body: { decision: v.decision, editedText: v.editedText } }),
    onMutate: (v) => ({ prev: dropFromList([v.approval.id]) }),
    onError: (e, _v, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(listKey, ctx.prev);
      toastFailure(e, t("approvals.actions.failed"));
    },
    onSettled: () => void invalidate("inboxCounts", "inbox", "rulesHealth"),
  });

  // batch decisions: one request for the unedited ones; edited drafts keep their own (edit-before-approve) call
  const batch = useMutation({
    mutationFn: (v: { ids: string[]; decision: "approve" | "reject" }) => api("decideApprovals", { body: v }),
    onMutate: (v) => ({ prev: dropFromList(v.ids) }),
    onSuccess: (r, v) => {
      const ok = r.results.filter((x) => x.ok).length;
      const failed = r.results.length - ok;
      if (failed) toast.warning(t("quick.approvals.batchPartial", { ok, failed }), { description: r.results.find((x) => !x.ok)?.error ?? undefined });
      else if (v.decision === "approve") toast.success(t("approvals.actions.batchDone", { count: ok }));
    },
    onError: (e, _v, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(listKey, ctx.prev);
      toastFailure(e, t("approvals.actions.failed"));
    },
    onSettled: () => void invalidate("approvals", "inboxCounts", "inbox", "rulesHealth"),
  });

  const textFor = (a: Approval) => edits[a.id] ?? a.draft ?? "";
  const isEdited = (a: Approval) => a.draft !== null && edits[a.id] !== undefined && edits[a.id] !== a.draft;
  const canApprove = (a: Approval) => !(a.draft !== null && !textFor(a).trim()) && [...textFor(a)].length <= MAX && !isWindowClosed(a, now);

  const approve = (a: Approval) => {
    if (!canApprove(a)) return;
    const edited = isEdited(a);
    decide.mutate(
      { approval: a, decision: "approve", editedText: edited ? textFor(a) : undefined },
      { onSuccess: () => toast.success(edited ? t("approvals.actions.approvedEdited") : t("approvals.actions.approved"), { duration: 2000 }) },
    );
  };

  /** Reject is not reversible on the server, so it waits out the Undo window before it is sent. */
  const rejectLater = (list: Approval[]) => {
    if (!list.length) return;
    const ids = list.map((a) => a.id);
    setHiddenIds((h) => new Set([...h, ...ids]));
    setSelected((s) => new Set([...s].filter((id) => !ids.includes(id))));
    const restore = () => setHiddenIds((h) => new Set([...h].filter((id) => !ids.includes(id))));
    const key = `reject:${ids.join(",")}`;
    const commit = deferCommit(
      key,
      () => {
        const done = () => {
          restore();
          void invalidate("approvals", "inboxCounts", "inbox", "rulesHealth");
        };
        if (ids.length === 1) void api("decideApproval", { params: { approvalId: ids[0]! }, body: { decision: "reject" } }).then(done, (e) => (restore(), toastFailure(e, t("approvals.actions.failed"))));
        else void api("decideApprovals", { body: { ids, decision: "reject" } }).then(done, (e) => (restore(), toastFailure(e, t("approvals.actions.failed"))));
      },
      UNDO_MS,
    );
    toastUndo({
      message: ids.length === 1 ? t("approvals.actions.rejected") : t("quick.approvals.batchRejected", { count: ids.length }),
      ms: UNDO_MS,
      undo: () => {
        if (commit.cancel()) restore();
        else throw new Error("already sent");
      },
    });
  };

  const approveMany = async (picked: Approval[]) => {
    const closed = picked.filter((a) => isWindowClosed(a, now));
    const sendable = picked.filter((a) => canApprove(a));
    if (!sendable.length) return;
    const counts = new Map<Approval["kind"], number>();
    for (const a of sendable) counts.set(a.kind, (counts.get(a.kind) ?? 0) + 1);
    const ok = await confirm({
      title: t("approvals.bulk.confirmTitle", { count: sendable.length }),
      description: (
        <span className="block space-y-2" data-testid="approve-summary">
          <span className="block">{t("approvals.bulk.confirmLead")}</span>
          <span className="block space-y-1">
            {[...counts].map(([kind, n]) => (
              <span key={kind} className="flex items-center gap-2 text-ink [&_svg]:size-4 [&_svg]:text-ink-3">
                {KIND_ICON[kind]}
                {t(`approvals.bulk.kind.${kind}`, { count: n })}
              </span>
            ))}
          </span>
          {closed.length > 0 && <span className="block text-warn">{t("approvals.bulk.skippedClosed", { count: closed.length })}</span>}
        </span>
      ),
      confirmLabel: t("approvals.bulk.confirm", { count: sendable.length }),
    });
    if (!ok) return;
    const edited = sendable.filter(isEdited);
    const plain = sendable.filter((a) => !isEdited(a));
    if (plain.length) batch.mutate({ ids: plain.map((a) => a.id), decision: "approve" });
    if (edited.length) {
      const results = await Promise.allSettled(edited.map((a) => decide.mutateAsync({ approval: a, decision: "approve", editedText: textFor(a) })));
      const done = results.filter((r) => r.status === "fulfilled").length;
      if (done && !plain.length) toast.success(t("approvals.actions.batchDone", { count: done }));
    }
  };

  const cardRefs = useRef<(HTMLElement | null)[]>([]);
  const focusCard = (i: number) => {
    setActive(i);
    cardRefs.current[i]?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  };
  const current = items[active];
  const toggle = (id: string, on?: boolean) =>
    setSelected((s) => {
      const n = new Set(s);
      if (on ?? !n.has(id)) n.add(id);
      else n.delete(id);
      return n;
    });
  // Q11: no bare `A` here (in the Inbox the same key assigns): approve is ⌘/Ctrl+Enter, so muscle memory never sends a reply
  useHotkeys(
    tab === "pending"
      ? {
          j: () => focusCard(Math.min(items.length - 1, active + 1)),
          k: () => focusCard(Math.max(0, active - 1)),
          "mod+Enter": (e) => {
            e.preventDefault();
            if (current) approve(current);
          },
          r: () => current && rejectLater([current]),
          e: () => cardRefs.current[active]?.querySelector("textarea")?.focus(),
          x: () => current && toggle(current.id),
          Escape: () => selected.size && setSelected(new Set()),
        }
      : {},
  );

  const selectedItems = items.filter((a) => selected.has(a.id));
  const allChecked = items.length > 0 && selected.size === items.length;
  const clearFilters = () => setSearch(NO_FILTERS);
  const ruleOptions = [{ value: "all", label: t("quick.approvals.anyRule") }, ...rules.map((r) => ({ value: r.id, label: `${r.icon ?? ""} ${r.name}` }))];
  const ageOptions = [
    { value: "all", label: t("quick.approvals.anyAge") },
    { value: "new", label: t("quick.approvals.ageNew") },
    { value: "day", label: t("quick.approvals.ageDay") },
    { value: "old", label: t("quick.approvals.ageOld") },
  ];
  const chips: FilterChip[] = [];
  const chip = (id: string, label: string, clear: Partial<ApprovalsSearch>) => chips.push({ id, label, onRemove: () => setSearch(clear) });
  if (search.kind) chip("kind", t(`approvals.kinds.${search.kind}`), { kind: undefined });
  if (search.accountId) chip("account", `‎@${accounts.find((a) => a.id === search.accountId)?.username ?? "…"}`, { accountId: undefined });
  if (search.postId) chip("post", `${t("quick.approvals.post")}: ${postOptions.posts.find((p) => p.postId === search.postId)?.caption?.slice(0, 24) ?? "…"}`, { postId: undefined });
  if (search.ruleId) chip("rule", `${t("quick.approvals.rule")}: ${ruleMap.get(search.ruleId)?.name ?? "…"}`, { ruleId: undefined });
  if (search.age) chip("age", ageOptions.find((o) => o.value === search.age)?.label ?? "", { age: undefined });

  return (
    <Page>
      <PageHeader title={t("approvals.title")} hint={t("approvals.description")} />
      <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
        <div className="mx-auto mb-3 flex max-w-4xl flex-wrap items-center gap-2">
          <TabsList
            value={tab}
            items={[
              { value: "pending", label: t("approvals.tabs.pending"), icon: <Clock />, count: tab === "pending" ? items.length : undefined },
              { value: "approved", label: t("approvals.tabs.approved"), icon: <Check /> },
              { value: "rejected", label: t("approvals.tabs.rejected"), icon: <X /> },
            ]}
          />
        </div>

        {/* calm filter row: Kind + Account visible; Post, Rule and Age behind Filters */}
        <div className="mx-auto max-w-4xl" data-testid="approvals-filters">
          <FilterBar
            label={t("quick.approvals.filters")}
            advancedCount={moreCount}
            chips={chips}
            onClear={clearFilters}
            quick={[
            <Select
              key="kind"
              size="sm"
              className="w-44"
              value={search.kind ?? "all"}
              onValueChange={(v) => setSearch({ kind: v === "all" ? undefined : (v as Approval["kind"]) })}
              aria-label={t("quick.approvals.kind")}
              options={[{ value: "all", label: t("quick.approvals.anyKind") }, ...KINDS.map((k) => ({ value: k, label: t(`approvals.kinds.${k}`) }))]}
            />,
            accounts.length > 1 && (
              <Select
                key="account"
                size="sm"
                className="w-44"
                value={search.accountId ?? "all"}
                onValueChange={(v) => setSearch({ accountId: v === "all" ? undefined : v })}
                aria-label={t("quick.approvals.account")}
                options={[{ value: "all", label: t("quick.approvals.anyAccount") }, ...accounts.map((a) => ({ value: a.id, label: `‎@${a.username}`, icon: <AccountAvatar account={a} size={16} /> }))]}
              />
            ),
            ]}
            advanced={
              <div className="grid w-full gap-3 sm:grid-cols-3">
                <FilterField label={t("quick.approvals.post")}>
                  <PostPicker posts={postOptions.posts} counts={postOptions.counts} value={search.postId} onChange={(postId) => setSearch({ postId })} anyLabel={t("quick.approvals.anyPost")} label={t("quick.approvals.post")} testId="approvals-post-filter" />
                </FilterField>
                <FilterField label={t("quick.approvals.rule")}>
                  <Select size="sm" value={search.ruleId ?? "all"} onValueChange={(v) => setSearch({ ruleId: v === "all" ? undefined : v })} aria-label={t("quick.approvals.rule")} options={ruleOptions} />
                </FilterField>
                <FilterField label={t("quick.approvals.age")}>
                  <Select size="sm" value={search.age ?? "all"} onValueChange={(v) => setSearch({ age: v === "all" ? undefined : (v as Age) })} aria-label={t("quick.approvals.age")} options={ageOptions} />
                </FilterField>
              </div>
            }
            end={
              <IconButton label={t("shell.topbar.shortcuts")} size="icon-sm" onClick={() => ui.set({ shortcuts: true })}>
                <Keyboard />
              </IconButton>
            }
          />
        </div>

        <TabsContent value={tab} forceMount className="mx-auto max-w-4xl pb-24 outline-none">
          {tab === "pending" && items.length > 0 && (
            <div className="mb-2 flex items-center gap-3 px-1 text-[12.5px] text-ink-3">
              <Checkbox data-testid="approvals-select-all" label={t("approvals.actions.selectAll")} checked={allChecked ? true : selected.size ? "indeterminate" : false} onCheckedChange={() => setSelected(allChecked || selected.size ? new Set() : new Set(items.map((a) => a.id)))} />
              <span className="ms-auto hidden md:inline">{t("approvals.keyboardHint", { mod: modKey })}</span>
            </div>
          )}
          {list.isLoading ? (
            <div className="space-y-3">
              {[0, 1, 2].map((i) => (
                <div key={i} className="skeleton h-52 rounded-[20px]" />
              ))}
            </div>
          ) : list.isError ? (
            <div className="card">
              <ErrorState error={list.error} onRetry={() => void list.refetch()} />
            </div>
          ) : items.length === 0 && filtered ? (
            <div className="card">
              <EmptyState variant="filtered" hint={t("approvals.empty.filteredHint")} onClearFilters={clearFilters} />
            </div>
          ) : items.length === 0 ? (
            <div className="card">
              {tab === "pending" ? (
                <EmptyState
                  icon={CheckCheck}
                  title={t("approvals.empty.title")}
                  hint={t("approvals.empty.hint")}
                  action={
                    <Button asChild variant="outline" size="sm" icon={<Workflow />}>
                      <Link to="/automation">{t("approvals.empty.openRules")}</Link>
                    </Button>
                  }
                />
              ) : (
                <EmptyState icon={Clock} title={t("approvals.empty.other")} hint={t("approvals.empty.otherHint")} />
              )}
            </div>
          ) : (
            <>
              <ul className="space-y-3">
                <AnimatePresence initial={false}>
                  {items.map((a, i) => (
                    <ApprovalCard
                      key={a.id}
                      ref={(el) => {
                        cardRefs.current[i] = el;
                      }}
                      approval={a}
                      rule={a.ruleId ? ruleMap.get(a.ruleId) : undefined}
                      active={tab === "pending" && i === active}
                      readOnly={tab !== "pending"}
                      text={textFor(a)}
                      edited={isEdited(a)}
                      onText={(v) => setEdits((e) => ({ ...e, [a.id]: v }))}
                      onReset={() =>
                        setEdits((e) => {
                          const n = { ...e };
                          delete n[a.id];
                          return n;
                        })
                      }
                      selected={selected.has(a.id)}
                      onSelect={(v) => toggle(a.id, v)}
                      onApprove={() => approve(a)}
                      onReject={() => rejectLater([a])}
                      onFocus={() => setActive(i)}
                      now={now}
                    />
                  ))}
                </AnimatePresence>
              </ul>
              {list.hasNextPage && (
                <div className="mt-4 flex justify-center">
                  <Button variant="outline" size="sm" loading={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()} data-testid="approvals-load-more">
                    {t("approvals.loadMore")}
                  </Button>
                </div>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* bulk bar: only after something is selected; approve-all lives here, behind a summary confirm */}
      <BulkBar
        count={tab === "pending" ? selectedItems.length : 0}
        onClear={() => setSelected(new Set())}
        actions={[
          { id: "approve", label: t("approvals.bulk.approve", { count: selectedItems.length }), icon: <CheckCheck />, primary: true, loading: batch.isPending && batch.variables?.decision === "approve", onClick: () => void approveMany(selectedItems) },
          { id: "reject", label: t("approvals.bulk.reject", { count: selectedItems.length }), icon: <X />, onClick: () => rejectLater(selectedItems) },
        ]}
      />
    </Page>
  );
}

/** What already happened to the comment being answered (deleted, hidden, a rule acted, replied…), compact. */
function TargetStatus({ commentId }: { commentId: string }) {
  const q = useApiQuery("commentDetail", { params: { commentId } }, { staleTime: 30_000, retry: false });
  if (!q.data) return null;
  return <CommentStatus comment={q.data.comment} variant="compact" className="ms-auto" />;
}

function FilterField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <div className="text-[12px] font-medium text-ink-3">{label}</div>
      {children}
    </div>
  );
}

type CardProps = {
  approval: Approval;
  rule: Rule | undefined;
  active: boolean;
  readOnly: boolean;
  text: string;
  edited: boolean;
  onText: (v: string) => void;
  onReset: () => void;
  selected: boolean;
  onSelect: (v: boolean) => void;
  onApprove: () => void;
  onReject: () => void;
  onFocus: () => void;
  now: number;
};

const ApprovalCard = function ApprovalCard({ ref, approval: a, rule, active, readOnly, text, edited, onText, onReset, selected, onSelect, onApprove, onReject, onFocus, now }: CardProps & { ref: (el: HTMLElement | null) => void }) {
  const { t } = useT();
  const reduce = useReducedMotion();
  const msLeft = a.expiresAt ? Date.parse(a.expiresAt) - now : null;
  const len = [...text].length;
  const ProposedIcon = a.proposedBy === "rule" ? Workflow : a.proposedBy === "assistant" ? Bot : User;
  const targetLink = a.target.id && (a.target.type === "comment" || a.target.type === "conversation") ? a.target.id : null;
  const windowClosed = isWindowClosed(a, now);
  const statusTone: Tone = a.status === "approved" ? "ok" : a.status === "edited" ? "accent" : a.status === "rejected" ? "bad" : a.status === "expired" ? "neutral" : "warn";
  const reason = a.reason ? reasonInWords(a.reason, t) : null;
  const hasMore = !!targetLink || edited || !!rule;

  return (
    <motion.li
      ref={ref}
      layout={!reduce}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: reduce ? 0 : 40, scale: 0.97, transition: { duration: 0.22 } }}
      data-testid="approval-card"
      data-id={a.id}
      onFocusCapture={onFocus}
      onClick={onFocus}
      className={cn("overflow-hidden rounded-[20px] border bg-surface shadow-xs transition-[border-color,box-shadow]", active ? "border-accent/50 shadow-card ring-2 ring-accent/15" : "border-line")}
    >
      <header className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-3">
        {!readOnly && <Checkbox checked={selected} onCheckedChange={(v) => onSelect(v === true)} aria-label={t("approvals.card.select")} data-testid="approval-select" />}
        <Badge tone={KIND_TONE[a.kind]} icon={KIND_ICON[a.kind]} size="md">
          {t(`approvals.kinds.${a.kind}`)}
        </Badge>
        {/* PR #16: "Drafted by <agent> v<n>" next to the kind */}
        <ApprovalAgentBadge agent={a.agent} />
        {rule ? (
          <Link to="/automation/rules/$ruleId" params={{ ruleId: rule.id }} className="inline-flex max-w-[220px] items-center gap-1.5 rounded-lg bg-surface-2 px-2 py-0.5 text-[12px] text-ink-2 hover:text-ink hover:underline" title={t("approvals.card.openRule")} data-testid="approval-rule">
            <Workflow className="size-3.5 shrink-0 text-ink-3" aria-hidden />
            <span className="truncate">{rule.name}</span>
          </Link>
        ) : (
          <span className="inline-flex items-center gap-1 text-[12px] text-ink-3">
            <ProposedIcon className="size-3.5" />
            {t(`approvals.proposedBy.${a.proposedBy}`)}
          </span>
        )}
        <span className="ms-auto flex items-center gap-2 text-[11.5px] text-ink-3">
          {readOnly ? (
            <>
              <Badge tone={statusTone}>{t(`approvals.status.${a.status}`)}</Badge>
              {a.execution && (
                <Badge tone={a.execution.status === "executed" ? "ok" : "bad"} icon={a.execution.status === "executed" ? <CheckCheck /> : <X />} data-testid="approval-execution" data-status={a.execution.status}>
                  {t(`approvals.execution.${a.execution.status}`)}
                </Badge>
              )}
            </>
          ) : (
            msLeft !== null && (
              <span className={cn("inline-flex items-center gap-1", msLeft < 2 * 3600_000 ? "font-semibold text-warn" : "")}>
                <Clock className="size-3.5" />
                {msLeft > 0 ? t("approvals.card.expiresIn", { time: fmtDuration(msLeft) }) : t("approvals.card.expired")}
              </span>
            )
          )}
          <time dateTime={a.createdAt}>{fmtRelative(a.createdAt, undefined, now)}</time>
        </span>
      </header>

      <div className="grid gap-0 md:grid-cols-2">
        {/* phones: the draft first (the thing to decide on), then the original */}
        <section className="order-2 border-line p-4 md:order-1 md:border-e">
          <div className="mb-2 flex items-center gap-2">
            {a.post && <PostThumb post={a.post} size={28} />}
            <h4 className="text-[12px] font-semibold text-ink-3">{t("approvals.card.original")}</h4>
            <TargetPerson type={a.target.type} id={a.target.id} className="text-[12px] font-semibold text-ink-2" />
            {a.target.type === "comment" && a.target.id && <TargetStatus commentId={a.target.id} />}
          </div>
          <blockquote className="rounded-2xl bg-surface-2 px-3.5 py-3 text-[14.5px] leading-relaxed text-ink" dir="auto">
            {a.target.preview}
          </blockquote>
          {reason && (
            <p className="mt-3 flex items-start gap-1.5 text-[12.5px] leading-relaxed text-ink-2" data-testid="approval-reason">
              <Sparkles className="mt-0.5 size-3.5 shrink-0 text-ink-3" />
              <span>
                <span className="font-semibold text-ink">{t("approvals.card.reason")}: </span>
                {reason}
              </span>
            </p>
          )}
        </section>
        <section className="order-1 border-b border-line p-4 md:order-2 md:border-b-0">
          <div className="mb-2 flex items-center gap-2">
            <h4 className="text-[12px] font-semibold text-ink-3">{t("approvals.card.draft")}</h4>
            {edited && (
              <Badge size="xs" tone="accent">
                {t("approvals.card.draftEdited")}
              </Badge>
            )}
          </div>
          {a.draft === null ? (
            <p className="rounded-2xl border border-dashed border-line-2 px-3.5 py-3 text-[13px] text-ink-3">{t("approvals.card.noDraft")}</p>
          ) : readOnly ? (
            <p className="rounded-2xl bg-surface-2 px-3.5 py-3 text-[14.5px] leading-relaxed text-ink" dir="auto">
              {a.draft}
            </p>
          ) : (
            <>
              <Textarea
                data-testid="draft-editor"
                value={text}
                onChange={(e) => onText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    onApprove();
                  }
                  if (e.key === "Escape") (e.target as HTMLTextAreaElement).blur();
                }}
                aria-label={t("approvals.card.draft")}
                dir="auto"
                rows={3}
                className="bg-surface text-[14.5px]"
              />
              <div className={cn("mt-1 text-end text-[11px]", len > MAX ? "font-semibold text-bad" : "text-ink-3")}>
                <Num>{t("approvals.card.chars", { count: len, max: MAX })}</Num>
              </div>
            </>
          )}
        </section>
      </div>

      {readOnly && a.execution?.status === "failed" && (
        <div className="border-t border-line px-4 py-3">
          {isReauth(a.execution) ? (
            <ReauthNotice testId="approval-reauth" />
          ) : (
            <p className="text-[12.5px] text-bad" data-testid="approval-execution-error">
              {t("approvals.execution.reason", { reason: a.execution.error ?? t("approvals.execution.failed") })}
            </p>
          )}
        </div>
      )}

      {!readOnly && windowClosed && (
        <div role="note" id={`approval-closed-${a.id}`} className="flex items-start gap-2 border-t border-line bg-warn-soft px-4 py-2.5 text-[12.5px] text-ink" data-testid="approval-window-closed">
          <TriangleAlert className="mt-0.5 size-4 shrink-0 text-warn" aria-hidden />
          <span>{windowClosedFor(a.windowExpiresAt ?? null, t, now)}</span>
        </div>
      )}

      {!readOnly && (
        <footer className="flex items-center justify-end gap-2 border-t border-line px-4 py-3">
          {hasMore && (
            <Menu>
              <MenuTrigger asChild>
                <IconButton label={t("approvals.card.more")} size="icon-sm" className="me-auto" data-testid="approval-more">
                  <MoreHorizontal />
                </IconButton>
              </MenuTrigger>
              <MenuContent align="start">
                {targetLink && (
                  <MenuItem asChild icon={<ArrowUpRight className="flip-rtl" />}>
                    <Link to="/inbox" search={{ id: targetLink }} data-testid="approval-open-target">
                      {t("approvals.card.openTarget")}
                    </Link>
                  </MenuItem>
                )}
                {edited && (
                  <MenuItem icon={<RotateCcw />} onSelect={onReset} data-testid="approval-reset">
                    {t("approvals.card.reset")}
                  </MenuItem>
                )}
                {rule && (
                  <MenuItem asChild icon={<Workflow />}>
                    <Link to="/automation/rules/$ruleId" params={{ ruleId: rule.id }}>
                      {t("approvals.card.openRule")}
                    </Link>
                  </MenuItem>
                )}
              </MenuContent>
            </Menu>
          )}
          <Button variant="ghost" icon={<X />} data-testid="reject-btn" onClick={onReject}>
            {t("approvals.actions.reject")}
          </Button>
          <Button
            variant="primary"
            icon={<Check />}
            data-testid="approve-btn"
            disabled={(a.draft !== null && !text.trim()) || len > MAX || windowClosed}
            aria-describedby={windowClosed ? `approval-closed-${a.id}` : undefined}
            title={`${modKey}+Enter`}
            onClick={onApprove}
          >
            {edited ? t("approvals.actions.approveEdited") : t("approvals.actions.approve")}
            <Kbd className="ms-1 hidden border-white/30 bg-white/15 text-white sm:inline-flex">{modKey}↵</Kbd>
          </Button>
        </footer>
      )}
    </motion.li>
  );
};
